import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Chip,
  Fade,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import {
  AutoAwesome,
  Refresh,
  Close,
  Edit,
  Check,
  Cancel,
  Visibility,
  VisibilityOff,
  Delete,
  CalendarToday,
  Add,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { safeFormatDate } from '../utils/dateUtils';
import { fetchWeeklySummaries, deleteSummary, renameSummary, triggerWeeklySummary } from '../store/slices/summariesSlice';
import EnhancedMarkdownRenderer from '../components/EnhancedMarkdownRenderer';
import backendUrl from '../store/config';

interface Summary {
  id: string;
  title: string;
  content: string;
  created_at: string;
  status: string;
  visible_to_users: boolean;
  start_date?: string;
  end_date?: string;
  date_range_display?: string;
  date_range?: string;
  summary_type?: string;
  source?: string;
}

const WeeklySummariesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Redux state
  const { weeklySummaries, weeklySummariesLoading, triggering } = useAppSelector((state: any) => state.summaries);
  const { user } = useAppSelector(state => state.auth);
  const token = useAppSelector((state: any) => state.auth.token);

  // Local UI state
  const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSummaryIds, setSelectedSummaryIds] = useState<Set<string>>(new Set());
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [summaryToDelete, setSummaryToDelete] = useState<Summary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [summaryToRename, setSummaryToRename] = useState<Summary | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [summarySource, setSummarySource] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('agrana.weeklySummaries.source');
      if (stored === 'starch' || stored === 'non_food' || stored === 'sns') {
        return stored;
      }
    } catch {
      // localStorage unavailable (private mode, SSR) — fall through to default
    }
    return 'starch';
  });

  useEffect(() => {
    try {
      localStorage.setItem('agrana.weeklySummaries.source', summarySource);
    } catch {
      // localStorage unavailable — persistence is best-effort
    }
  }, [summarySource]);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [triggerStartDate, setTriggerStartDate] = useState<Date | null>(null);
  const [triggerEndDate, setTriggerEndDate] = useState<Date | null>(null);
  const [triggerSources, setTriggerSources] = useState<string[]>(['starch', 'non_food', 'sns']);

  const loading = weeklySummariesLoading || refreshLoading;

  useEffect(() => {
    dispatch(fetchWeeklySummaries(summarySource));
  }, [dispatch, summarySource]);

  // Handle checkbox selection
  const handleSummarySelect = (summaryId: string, checked: boolean) => {
    const newSelected = new Set(selectedSummaryIds);
    if (checked) {
      newSelected.add(summaryId);
    } else {
      newSelected.delete(summaryId);
    }
    setSelectedSummaryIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<string>(completedSummaries.map((s: Summary) => s.id));
      setSelectedSummaryIds(allIds);
    } else {
      setSelectedSummaryIds(new Set<string>());
    }
  };

  const updateSummariesVisibility = async () => {
    setUpdatingVisibility(true);
    try {
      const allSummaryIds = completedSummaries.map((s: Summary) => s.id);

      if (selectedSummaryIds.size > 0) {
        const makeVisibleResponse = await fetch(`${backendUrl}/api/summaries/visibility`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            summary_ids: Array.from(selectedSummaryIds),
            visible_to_users: true
          })
        });
        if (!makeVisibleResponse.ok) {
          throw new Error('Failed to make summaries visible');
        }
      }

      const unselectedIds = allSummaryIds.filter((id: string) => !selectedSummaryIds.has(id));
      if (unselectedIds.length > 0) {
        const makeHiddenResponse = await fetch(`${backendUrl}/api/summaries/visibility`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            summary_ids: unselectedIds,
            visible_to_users: false
          })
        });
        if (!makeHiddenResponse.ok) {
          throw new Error('Failed to hide summaries');
        }
      }

      setSnackbarMessage(`Visibility updated: ${selectedSummaryIds.size} visible, ${unselectedIds.length} hidden`);
      setSnackbarOpen(true);
      dispatch(fetchWeeklySummaries(summarySource));
      setSelectedSummaryIds(new Set());
      setEditMode(false);
    } catch (error) {
      console.error('Error updating visibility:', error);
      setSnackbarMessage('Failed to update summary visibility');
      setSnackbarOpen(true);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const handleDeleteClick = (summary: Summary) => {
    setSummaryToDelete(summary);
    setDeleteDialogOpen(true);
  };

  const handleRenameClick = (summary: Summary, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSummaryToRename(summary);
    setRenameTitle(summary.title || '');
    setRenameDialogOpen(true);
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setSummaryToRename(null);
    setRenameTitle('');
  };

  const handleRenameConfirm = async () => {
    if (!summaryToRename) return;
    const nextTitle = renameTitle.trim();
    if (!nextTitle) {
      setSnackbarMessage('Title cannot be empty');
      setSnackbarOpen(true);
      return;
    }

    setRenaming(true);
    try {
      const updated = await dispatch(renameSummary({ id: summaryToRename.id, title: nextTitle })).unwrap();
      setSnackbarMessage('Summary renamed successfully');
      setSnackbarOpen(true);
      setRenameDialogOpen(false);
      setSummaryToRename(null);
      setRenameTitle('');
      setSelectedSummary((prev) => (prev?.id === updated.id ? { ...prev, title: updated.title } : prev));
    } catch (error) {
      console.error('Error renaming summary:', error);
      setSnackbarMessage(typeof error === 'string' ? error : 'Failed to rename summary');
      setSnackbarOpen(true);
    } finally {
      setRenaming(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!summaryToDelete) return;
    setDeleting(true);
    try {
      await dispatch(deleteSummary(summaryToDelete.id)).unwrap();
      setSnackbarMessage(`Summary "${summaryToDelete.title}" deleted successfully`);
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setSummaryToDelete(null);
      if (selectedSummary?.id === summaryToDelete.id) {
        setSelectedSummary(null);
      }
      dispatch(fetchWeeklySummaries(summarySource));
    } catch (error) {
      console.error('Error deleting summary:', error);
      setSnackbarMessage('Failed to delete summary');
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSummaryToDelete(null);
  };

  const completedSummaries = (weeklySummaries || [])
    .filter((summary: Summary) => summary.status === 'completed')
    .sort((a: Summary, b: Summary) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        height: '100%',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Fade in timeout={600}>
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {/* Summary Selection Sidebar */}
          <Box
            sx={{
              width: isMobile ? '100%' : (isTablet ? 280 : 300),
              minWidth: isMobile ? 'auto' : (isTablet ? 270 : 290),
              height: isMobile ? 'auto' : '100%',
              maxHeight: isMobile ? '50vh' : '100%',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1050,
              borderRight: isMobile ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
              borderBottom: isMobile ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: isMobile ? 1.5 : 2,
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 1.5 : 2,
                scrollbarGutter: 'stable',
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.3)' },
              }}
            >
              {/* Source Selector */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                  DATA SOURCE
                </Typography>
                <ToggleButtonGroup
                  value={summarySource}
                  exclusive
                  onChange={(_, newSource) => {
                    if (newSource !== null) setSummarySource(newSource);
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      py: 0.5,
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: '#a31830',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#8a1428' },
                      },
                    },
                  }}
                >
                  <ToggleButton value="starch">Starch</ToggleButton>
                  <ToggleButton value="non_food">Non-Food</ToggleButton>
                  <ToggleButton value="sns">SNS</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Weekly Summaries List */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobile ? "center" : "space-between",
                    position: "relative",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textAlign: isMobile ? "center" : "left",
                      flex: isMobile ? 1 : "unset",
                      fontSize: isMobile ? "0.7rem" : "0.9rem",
                    }}
                  >
                    Weekly Newsletters ({completedSummaries.length})
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                      <Tooltip title={editMode ? "Cancel edit" : "Edit summaries"}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (editMode) {
                              setEditMode(false);
                              setSelectedSummaryIds(new Set());
                            } else {
                              setEditMode(true);
                              const visibleSummaryIds = completedSummaries
                                .filter((summary: { visible_to_users: any; }) => summary.visible_to_users)
                                .map((summary: { id: any; }) => summary.id);
                              setSelectedSummaryIds(new Set(visibleSummaryIds));
                            }
                          }}
                          sx={{ p: 0.5 }}
                        >
                          {editMode ? <Cancel sx={{ fontSize: isMobile ? 18 : 20 }} /> : <Edit sx={{ fontSize: isMobile ? 18 : 20 }} />}
                        </IconButton>
                      </Tooltip>
                    )}

                    {user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                      <Tooltip title="Generate weekly summary">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const end = new Date();
                            const start = new Date();
                            start.setDate(start.getDate() - 7);
                            setTriggerStartDate(start);
                            setTriggerEndDate(end);
                            setTriggerSources([summarySource]);
                            setTriggerDialogOpen(true);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <Add sx={{ fontSize: isMobile ? 22 : 24 }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Refresh">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          setRefreshLoading(true);
                          const startTime = Date.now();
                          try {
                            await dispatch(fetchWeeklySummaries(summarySource));
                          } catch (error) {
                            console.error("Error refreshing data:", error);
                          }
                          const elapsedTime = Date.now() - startTime;
                          const remainingTime = Math.max(0, 1500 - elapsedTime);
                          if (remainingTime > 0) {
                            await new Promise((resolve) => setTimeout(resolve, remainingTime));
                          }
                          setRefreshLoading(false);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <Refresh sx={{ fontSize: isMobile ? 22 : 24 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Edit mode controls */}
                {editMode && user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSummaryIds.size === completedSummaries.length && completedSummaries.length > 0}
                            indeterminate={selectedSummaryIds.size > 0 && selectedSummaryIds.size < completedSummaries.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Select All"
                      />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {selectedSummaryIds.size} selected
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Check />}
                        onClick={updateSummariesVisibility}
                        disabled={updatingVisibility}
                        sx={{ minWidth: 'auto' }}
                      >
                        Update Visibility
                      </Button>
                    </Box>
                  </Box>
                )}

                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Loading summaries...
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {completedSummaries.map((summary: Summary) => (
                    <Card
                      key={summary.id}
                      sx={{
                        cursor: editMode ? 'default' : 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: editMode ? 'none' : 'translateY(-2px)',
                          boxShadow: (theme) => editMode ? theme.shadows[1] : theme.shadows[4]
                        },
                        border: '2px solid',
                        borderColor: (editMode && selectedSummaryIds.has(summary.id)) || (!editMode && selectedSummary?.id === summary.id) ? 'primary.main' : 'transparent'
                      }}
                      onClick={() => !editMode && setSelectedSummary(summary)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: isMobile ? 'center' : 'left', position: 'relative' }}>
                        {user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                          <Box sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            zIndex: 1
                          }}>
                            {!editMode && (
                              <Tooltip title="Rename summary">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleRenameClick(summary, e)}
                                  sx={{ p: 0.25 }}
                                  aria-label="Rename summary"
                                >
                                  <Edit sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {editMode && (
                              <Tooltip title="Delete summary">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(summary)}
                                  sx={{
                                    p: 0.25,
                                    '&:hover': { color: 'error.contrastText' }
                                  }}
                                >
                                  <Delete sx={{ fontSize: 16, color: 'error.main' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={summary.visible_to_users ? 'Visible to users' : 'Hidden from users'}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {summary.visible_to_users ? (
                                  <Visibility sx={{ fontSize: 16, color: 'success.main' }} />
                                ) : (
                                  <VisibilityOff sx={{ fontSize: 16, color: 'text.disabled' }} />
                                )}
                              </Box>
                            </Tooltip>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {editMode && user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                            <Checkbox
                              checked={selectedSummaryIds.has(summary.id)}
                              onChange={(e) => handleSummarySelect(summary.id, e.target.checked)}
                              size="small"
                              sx={{ mt: -0.5 }}
                            />
                          )}
                          <Box sx={{ flex: 1, pr: user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) ? 5 : 0 }}>
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                {summary.title}
                              </Typography>
                            </Box>
                            {summary.date_range_display && (
                              <Chip
                                icon={<CalendarToday />}
                                label={summary.date_range_display}
                                size="small"
                                sx={{ mb: 1, fontSize: '0.75rem' }}
                              />
                            )}
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              {summary.content.substring(0, 80)}...
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                              {safeFormatDate(summary.created_at, 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  {completedSummaries.length === 0 && !loading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No weekly newsletters yet
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        Newsletters are auto-generated every week
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? 'auto' : '100%',
            minHeight: isMobile ? '50vh' : 'auto',
            overflow: 'hidden'
          }}>
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              p: isMobile ? 2 : 4,
              overflow: 'hidden'
            }}>
              {!selectedSummary ? (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  textAlign: 'center',
                  maxWidth: isMobile ? '100%' : 600,
                  mx: 'auto',
                  px: isMobile ? 2 : 0
                }}>
                  <Box>
                    <AutoAwesome sx={{ fontSize: isMobile ? 60 : 80, color: 'primary.main', mb: 2 }} />
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        fontSize: isMobile ? '1.5rem' : '2.125rem'
                      }}
                    >
                      Weekly Newsletters
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        mb: 4,
                        lineHeight: 1.6,
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}
                    >
                      {completedSummaries.length > 0
                        ? 'Select a newsletter to display here'
                        : 'No weekly newsletters yet. They are auto-generated every week.'
                      }
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'auto'
                }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 3,
                    pb: 2,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                  }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedSummary.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {safeFormatDate(selectedSummary.created_at, 'MMM dd, yyyy HH:mm')}
                        </Typography>
                        {selectedSummary.date_range_display && (
                          <Chip
                            icon={<CalendarToday />}
                            label={selectedSummary.date_range_display}
                            size="small"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Button
                      onClick={() => setSelectedSummary(null)}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      <Close />
                    </Button>
                  </Box>

                  <EnhancedMarkdownRenderer
                    content={selectedSummary.content}
                    variant="full"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Summary</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the summary "{summaryToDelete?.title}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            autoFocus
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Summary Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleRenameCancel}
        aria-labelledby="rename-summary-dialog-title"
      >
        <DialogTitle id="rename-summary-dialog-title">Rename Summary</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a new title for "{summaryToRename?.title}".
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            disabled={renaming}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel} disabled={renaming}>Cancel</Button>
          <Button onClick={handleRenameConfirm} variant="contained" disabled={renaming}>
            {renaming ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Weekly Summary Dialog */}
      <Dialog
        open={triggerDialogOpen}
        onClose={() => !triggering && setTriggerDialogOpen(false)}
        aria-labelledby="trigger-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="trigger-dialog-title">Generate Weekly Summary</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a date range and sources to generate weekly summaries. Processing takes a few minutes.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <DatePicker
              label="Start Date"
              value={triggerStartDate}
              onChange={(date: Date | null) => setTriggerStartDate(date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label="End Date"
              value={triggerEndDate}
              onChange={(date: Date | null) => setTriggerEndDate(date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Sources</Typography>
              <ToggleButtonGroup
                value={triggerSources}
                onChange={(_e, newSources: string[]) => {
                  if (newSources.length > 0) setTriggerSources(newSources);
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="starch">Starch</ToggleButton>
                <ToggleButton value="non_food">Non-Food</ToggleButton>
                <ToggleButton value="sns">SNS</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerDialogOpen(false)} disabled={triggering}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!triggerStartDate || !triggerEndDate) {
                setSnackbarMessage('Please select both start and end dates');
                setSnackbarOpen(true);
                return;
              }
              try {
                await dispatch(triggerWeeklySummary({
                  start_date: format(triggerStartDate, 'yyyy-MM-dd'),
                  end_date: format(triggerEndDate, 'yyyy-MM-dd'),
                  sources: triggerSources,
                })).unwrap();
                setSnackbarMessage('Weekly summary generation triggered. Processing will take a few minutes.');
                setSnackbarOpen(true);
                setTriggerDialogOpen(false);
                setTimeout(() => dispatch(fetchWeeklySummaries(summarySource)), 3000);
              } catch (error) {
                setSnackbarMessage(typeof error === 'string' ? error : 'Failed to trigger weekly summary generation');
                setSnackbarOpen(true);
              }
            }}
            variant="contained"
            disabled={triggering || !triggerStartDate || !triggerEndDate}
          >
            {triggering ? <CircularProgress size={20} color="inherit" /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMessage.includes('Failed') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeeklySummariesPage;
